/**
 * EDIT MEMBER PAGE
 * Allows editing of member information
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EditMemberPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      idNumber: '',
      gender: '',
      phoneNumber: '',
      email: '',
      dateOfBirth: '',
      physicalAddress: '',
      city: '',
      homeCounty: '',
      employmentStatus: '',
      employerName: ''
    },
    saccoInfo: {
      membershipType: 'Regular',
      status: 'Active',
      department: '',
      payrollNumber: ''
    },
    nextOfKin: {
      name: '',
      phoneNumber: '',
      relationship: '',
      idNumber: '',
      physicalAddress: '',
      benefitPercentage: ''
    }
  });

  // Other next of kin state
  const [otherNextOfKin, setOtherNextOfKin] = useState([]);

  useEffect(() => {
    fetchMemberData();
  }, [id]);

  const fetchMemberData = async () => {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/members/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch member');

      const data = await response.json();
      const member = data.data;

      // Format date for input field - FIXED
      let dateOfBirth = '';
      if (member.personalInfo?.dateOfBirth) {
        try {
          let date;
          if (member.personalInfo.dateOfBirth.toDate && typeof member.personalInfo.dateOfBirth.toDate === 'function') {
            date = member.personalInfo.dateOfBirth.toDate();
          } else if (member.personalInfo.dateOfBirth._seconds) {
            date = new Date(member.personalInfo.dateOfBirth._seconds * 1000);
          } else {
            date = new Date(member.personalInfo.dateOfBirth);
          }
          
          if (!isNaN(date.getTime())) {
            dateOfBirth = date.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Date parsing error:', e);
          dateOfBirth = '';
        }
      }

      setFormData({
        personalInfo: {
          fullName: member.personalInfo?.fullName || '',
          idNumber: member.personalInfo?.idNumber || '',
          gender: member.personalInfo?.gender || '',
          phoneNumber: member.personalInfo?.phoneNumber || '',
          email: member.personalInfo?.email || '',
          dateOfBirth: dateOfBirth,
          physicalAddress: member.personalInfo?.physicalAddress || '',
          city: member.personalInfo?.city || '',
          homeCounty: member.personalInfo?.homeCounty || '',
          employmentStatus: member.personalInfo?.employmentStatus || '',
          employerName: member.personalInfo?.employerName || ''
        },
        saccoInfo: {
          membershipType: member.saccoInfo?.membershipType || 'Regular',
          status: member.saccoInfo?.status || 'Active',
          department: member.saccoInfo?.department || '',
          payrollNumber: member.saccoInfo?.payrollNumber || ''
        },
        nextOfKin: {
          name: member.nextOfKin?.name || '',
          phoneNumber: member.nextOfKin?.phoneNumber || '',
          relationship: member.nextOfKin?.relationship || '',
          idNumber: member.nextOfKin?.idNumber || '',
          physicalAddress: member.nextOfKin?.physicalAddress || '',
          benefitPercentage: member.nextOfKin?.benefitPercentage ?? ''
        }
      });

      // Populate otherNextOfKin if present
      if (Array.isArray(member.otherNextOfKin) && member.otherNextOfKin.length > 0) {
        setOtherNextOfKin(member.otherNextOfKin.map(k => ({
          name: k.name || '',
          relationship: k.relationship || '',
          benefitPercentage: k.benefitPercentage ?? ''
        })));
      }

    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load member data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Other Next of Kin handlers
  const addOtherKin = () => setOtherNextOfKin(prev => [...prev, { name: '', relationship: '', benefitPercentage: '' }]);
  const removeOtherKin = (idx) => setOtherNextOfKin(prev => prev.filter((_, i) => i !== idx));
  const handleOtherKinChange = (idx, field, value) => {
    setOtherNextOfKin(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const token = localStorage.getItem('hopeSaccoToken');
      
      const response = await fetch(`${API_URL}/members/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, otherNextOfKin: otherNextOfKin.filter(k => k.name.trim()) })
      });

      if (!response.ok) throw new Error('Failed to update member');

      const data = await response.json();
      setSuccess('Member updated successfully!');
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/admin/members/${id}`);
      }, 1500);

    } catch (error) {
      console.error('Error:', error);
      setError('Failed to update member. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading member data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      activePage="members" 
      pageTitle="Edit Member"
      breadcrumbs={[
        { label: 'Members', link: '/admin/members' },
        { label: 'Edit Member' }
      ]}
    >
      <div className="edit-member-page">
        <div className="page-header">
          <div className="header-left">
            <h1>Edit Member</h1>
            <div className="breadcrumb">
              <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer' }}>Dashboard</span>
              <span className="separator">›</span>
              <span onClick={() => navigate('/admin/members')} style={{ cursor: 'pointer' }}>Members</span>
              <span className="separator">›</span>
              <span className="current">Edit Member</span>
            </div>
          </div>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate(`/admin/members/${id}`)}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            ✓ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-member-form">
          {/* Personal Information */}
          <div className="form-section">
            <h2>Personal Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.personalInfo.fullName}
                  onChange={(e) => handleInputChange('personalInfo', 'fullName', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>ID Number *</label>
                <input
                  type="text"
                  value={formData.personalInfo.idNumber}
                  onChange={(e) => handleInputChange('personalInfo', 'idNumber', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.personalInfo.phoneNumber}
                  onChange={(e) => handleInputChange('personalInfo', 'phoneNumber', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.personalInfo.email}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={formData.personalInfo.dateOfBirth}
                  onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Employment Status</label>
                <select
                  value={formData.personalInfo.employmentStatus}
                  onChange={(e) => handleInputChange('personalInfo', 'employmentStatus', e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="Employed">Employed</option>
                  <option value="Self-Employed">Self-Employed</option>
                  <option value="Unemployed">Unemployed</option>
                  <option value="Retired">Retired</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Physical Address</label>
                <input
                  type="text"
                  value={formData.personalInfo.physicalAddress}
                  onChange={(e) => handleInputChange('personalInfo', 'physicalAddress', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Gender</label>
                <select
                  value={formData.personalInfo.gender}
                  onChange={(e) => handleInputChange('personalInfo', 'gender', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="form-group">
                <label>City / Town</label>
                <input
                  type="text"
                  value={formData.personalInfo.city}
                  onChange={(e) => handleInputChange('personalInfo', 'city', e.target.value)}
                  placeholder="e.g. Nakuru"
                />
              </div>

              <div className="form-group">
                <label>Home County</label>
                <input
                  type="text"
                  value={formData.personalInfo.homeCounty}
                  onChange={(e) => handleInputChange('personalInfo', 'homeCounty', e.target.value)}
                  placeholder="e.g. Nakuru"
                />
              </div>

              <div className="form-group full-width">
                <label>Employer Name</label>
                <input
                  type="text"
                  value={formData.personalInfo.employerName}
                  onChange={(e) => handleInputChange('personalInfo', 'employerName', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* SACCO Information */}
          <div className="form-section">
            <h2>SACCO Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Membership Type</label>
                <select
                  value={formData.saccoInfo.membershipType}
                  onChange={(e) => handleInputChange('saccoInfo', 'membershipType', e.target.value)}
                >
                  <option value="Regular">Regular</option>
                  <option value="Premium">Premium</option>
                  <option value="Corporate">Corporate</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.saccoInfo.status}
                  onChange={(e) => handleInputChange('saccoInfo', 'status', e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>

              <div className="form-group">
                <label>Department</label>
                <select
                  value={formData.saccoInfo.department}
                  onChange={(e) => handleInputChange('saccoInfo', 'department', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Senior School">Senior School</option>
                  <option value="Prep School">Prep School</option>
                  <option value="Work Force">Work Force</option>
                  <option value="Alumni">Alumni</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payroll Number</label>
                <input
                  type="text"
                  value={formData.saccoInfo.payrollNumber}
                  onChange={(e) => handleInputChange('saccoInfo', 'payrollNumber', e.target.value)}
                  placeholder="e.g. 696"
                />
              </div>
            </div>
          </div>

          {/* Next of Kin */}
          <div className="form-section">
            <h2>Next of Kin</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.nextOfKin.name}
                  onChange={(e) => handleInputChange('nextOfKin', 'name', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Relationship *</label>
                <select
                  value={formData.nextOfKin.relationship}
                  onChange={(e) => handleInputChange('nextOfKin', 'relationship', e.target.value)}
                  required
                >
                  <option value="">Select Relationship</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Child">Child</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.nextOfKin.phoneNumber}
                  onChange={(e) => handleInputChange('nextOfKin', 'phoneNumber', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>ID Number</label>
                <input
                  type="text"
                  value={formData.nextOfKin.idNumber}
                  onChange={(e) => handleInputChange('nextOfKin', 'idNumber', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <label>Physical Address</label>
                <input
                  type="text"
                  value={formData.nextOfKin.physicalAddress}
                  onChange={(e) => handleInputChange('nextOfKin', 'physicalAddress', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Benefit Percentage (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.nextOfKin.benefitPercentage}
                  onChange={(e) => handleInputChange('nextOfKin', 'benefitPercentage', e.target.value)}
                  placeholder="e.g. 50"
                />
              </div>
            </div>

            {/* Other Next of Kin */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>Other Beneficiaries</h3>
                <button type="button" className="btn btn-secondary" onClick={addOtherKin} style={{ fontSize: 13 }}>
                  + Add
                </button>
              </div>
              {otherNextOfKin.length === 0 && (
                <p style={{ color: '#888', fontSize: 13, margin: 0 }}>None added.</p>
              )}
              {otherNextOfKin.map((kin, idx) => (
                <div key={idx} style={{ border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px', marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <strong style={{ fontSize: 13 }}>Beneficiary {idx + 1}</strong>
                    <button type="button" onClick={() => removeOtherKin(idx)} style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 13 }}>✕ Remove</button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Full Name</label>
                      <input type="text" value={kin.name} onChange={(e) => handleOtherKinChange(idx, 'name', e.target.value)} placeholder="Full name" />
                    </div>
                    <div className="form-group">
                      <label>Relationship</label>
                      <select value={kin.relationship} onChange={(e) => handleOtherKinChange(idx, 'relationship', e.target.value)}>
                        <option value="">Select</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Parent">Parent</option>
                        <option value="Child">Child</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Benefit %</label>
                      <input type="number" min="0" max="100" value={kin.benefitPercentage} onChange={(e) => handleOtherKinChange(idx, 'benefitPercentage', e.target.value)} placeholder="e.g. 25" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/admin/members/${id}`)}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default EditMemberPage;
